import React, { FC, Suspense, useCallback, useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTitle } from 'react-use';

import { ActionCreators as fileAttachmentsActionCreators } from '@savgroup-front-common/core/domains/attachments';
import { safeFormattedIntlString } from '@savgroup-front-common/core/formatters';
import { buildPageTitle } from '@savgroup-front-common/core/helpers';
import { GetCarrierLabelsInfosQuery } from '@savgroup-front-common/core/hooks/useGetCarrierLabelsInfosQuery';
import useDocumentSubscription from '@savgroup-front-common/core/hooks/useGetFileDocuments/useDocumentSubscription';
import { GetFullClaimContextQuery } from '@savgroup-front-common/core/hooks/useGetFullClaimContextQuery';
import { useSubscription } from '@savgroup-front-common/core/hooks/useSubscription';
import { BaseLoader } from '@savgroup-front-common/core/molecules/BaseLoader';

import BanneredLayout from '@/view/app/NewLayout/BanneredLayout/BanneredLayout';

import { rehydrateFileTrackingPage } from '../../../domains/FileTrackingPage/actionCreators';
import { GetFileStatesQuery } from '../../app/hooks/Workflow/useGetFileStatesQuery';
import { useRedirectToNonNewDesignRoute } from '../irshPages/hooks/useRedirectToNonNewDesignRoute';

import FileFollowups from './FileFollowups/FileFollowups';
import {
  OnStateUpdateOperationName,
  onStateUpdateQuery,
} from './FileFollowupsPage.gql';
import {
  useBreadCrumbs,
  useGetProductsDetails,
} from './FileFollowupsPage.hooks';
import { GetClaimContextHandlingQuery } from './hooks/useGetClaimContextHandlingQuery';
import { GetClaimContextSummaryQuery } from './hooks/useGetClaimContextSummaryQuery';
import messages from './messages';

const FileFollowupsPage: FC = () => {
  const { fileId } = useParams() as { fileId: string };
  const queryClient = useQueryClient();
  const {
    productsDetails,
    isLoadingSolution,
    isLoadingModel,
    isLoadingFileSummary,
  } = useGetProductsDetails();

  useRedirectToNonNewDesignRoute();

  useTitle(buildPageTitle([messages.fileFollowupsTitlePage]));

  const isLoading = isLoadingSolution || isLoadingModel || isLoadingFileSummary;

  const breadCrumbs = useBreadCrumbs({ isLoading, productsDetails, fileId });

  const dispatch = useDispatch();

  const handleRefetchQueries = useCallback(() => {
    return Promise.all([
      queryClient.invalidateQueries(GetClaimContextSummaryQuery),
      queryClient.invalidateQueries(GetClaimContextHandlingQuery),
      queryClient.invalidateQueries(GetFileStatesQuery),
      queryClient.invalidateQueries(GetFullClaimContextQuery),
      dispatch(
        rehydrateFileTrackingPage({
          fileId,
        }),
      ),
    ]);
  }, [dispatch, fileId, queryClient]);

  const handleRefetchDocumentsQueries = useCallback(() => {
    dispatch(
      fileAttachmentsActionCreators.refreshDocumentsStatusByFileId({ fileId }),
    );

    return Promise.all([
      queryClient.invalidateQueries(GetFullClaimContextQuery),
      queryClient.invalidateQueries(GetCarrierLabelsInfosQuery),
    ]);
  }, [dispatch, fileId, queryClient]);

  useEffect(() => {
    dispatch(
      rehydrateFileTrackingPage({
        fileId,
      }),
    );
  }, [dispatch, fileId]);

  useSubscription({
    query: onStateUpdateQuery,
    variables: { fileId },
    onNext: handleRefetchQueries,
    operationName: OnStateUpdateOperationName,
  });

  useDocumentSubscription({
    onRefetchFileDocuments: handleRefetchDocumentsQueries,
    fileSummary: { fileId },
  });

  if (isLoading || !productsDetails) {
    return <BaseLoader />;
  }

  return (
    <BanneredLayout>
      <BanneredLayout.BannerTitle
        breadcrumbs={breadCrumbs}
        title={safeFormattedIntlString(messages.bannerTitle, {
          reference: productsDetails[0]?.fileReference,
        })}
        description={messages.bannerDescription}
      />

      <BanneredLayout.Content>
        <Suspense fallback={<BaseLoader />}>
          <FileFollowups />
        </Suspense>
      </BanneredLayout.Content>
    </BanneredLayout>
  );
};

export default React.memo(FileFollowupsPage);
